import { useState } from 'react'
import { createDeck, deleteDeck } from '@/api/decks'
import { cn } from '@/utils/cn'
import type { Deck } from '@/types/deck'
import { filledFill, focusRing, quietFill, softFill } from '@/components/ui/buttonStyles'

interface DeckStripProps {
  decks: Deck[]
  languageId: number
  onStudy: (deckId: number) => void
  onChanged: () => void
}

/** Horizontal strip of phrase-deck cards on the vocabulary page. */
export function DeckStrip({ decks, languageId, onStudy, onChanged }: DeckStripProps) {
  const [creating, setCreating] = useState(false)
  const [name, setName] = useState('')
  const [error, setError] = useState<string | null>(null)

  function handleCreate() {
    const trimmed = name.trim()
    if (!trimmed) return
    setError(null)
    createDeck(languageId, trimmed)
      .then(() => {
        setCreating(false)
        setName('')
        onChanged()
      })
      .catch(() => setError('Could not create the deck.'))
  }

  return (
    <div>
      <div className="flex items-center justify-between gap-4">
        <h2 className="text-xs font-medium uppercase tracking-wider text-muted">
          Phrase decks
        </h2>
        {!creating && (
          <button
            type="button"
            onClick={() => setCreating(true)}
            className={cn(
              'inline-flex items-center min-h-11 px-4 rounded-full text-xs font-normal transition-colors duration-200',
              softFill,
              focusRing,
            )}
          >
            + New deck
          </button>
        )}
      </div>

      {creating && (
        <div className="mt-3 flex items-center gap-2">
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
            placeholder="Deck name"
            aria-label="New deck name"
            autoFocus
            className={cn(
              'flex-1 max-w-xs rounded-md border border-border-divider bg-surface-bg px-4 py-2.5',
              'text-sm text-body font-light placeholder:text-label/60',
              'focus-visible:outline-none focus-visible:border-accent focus-visible:ring-2 focus-visible:ring-accent/30',
              'transition-colors duration-200',
            )}
          />
          <button
            type="button"
            onClick={handleCreate}
            className={`shrink-0 text-xs font-normal transition-colors duration-200 min-h-11 px-4 rounded-full ${softFill} ${focusRing}`}
          >
            Create
          </button>
          <button
            type="button"
            onClick={() => { setCreating(false); setName(''); setError(null) }}
            className={`shrink-0 text-xs font-normal transition-colors duration-200 min-h-11 px-4 rounded-full ${quietFill} ${focusRing}`}
          >
            Cancel
          </button>
          {error && <p role="alert" className="text-xs text-error font-light">{error}</p>}
        </div>
      )}

      {decks.length === 0 && !creating ? (
        <p className="mt-3 text-sm text-muted font-light">
          No decks yet. Create one, or add a phrase to get started.
        </p>
      ) : (
        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
          {decks.map((deck) => (
            <DeckCard key={deck.id} deck={deck} onStudy={onStudy} onChanged={onChanged} />
          ))}
        </div>
      )}
    </div>
  )
}

function DeckCard({ deck, onStudy, onChanged }: {
  deck: Deck
  onStudy: (deckId: number) => void
  onChanged: () => void
}) {
  const [confirming, setConfirming] = useState(false)

  function handleDelete() {
    if (!confirming) {
      setConfirming(true)
      return
    }
    deleteDeck(deck.id).then(onChanged).catch(() => setConfirming(false))
  }

  return (
    <div className="rounded-lg border border-border-divider bg-surface-bg/30 px-4 py-3">
      <div className="flex items-baseline justify-between gap-2">
        <p className="font-serif text-base font-medium text-heading truncate">{deck.name}</p>
        <span className="shrink-0 text-xs text-muted font-light">
          {deck.card_count} {deck.card_count === 1 ? 'phrase' : 'phrases'}
        </span>
      </div>
      <div className="mt-2 flex items-center gap-3">
        <button
          type="button"
          onClick={() => onStudy(deck.id)}
          disabled={deck.due_count === 0}
          className={cn(
            'inline-flex items-center text-xs font-normal px-4 rounded-full min-h-11',
            'transition-colors duration-200 disabled:opacity-40',
            filledFill,
            focusRing,
          )}
        >
          Study{deck.due_count > 0 && ` (${deck.due_count} due)`}
        </button>
        <button
          type="button"
          onClick={handleDelete}
          className={cn(
            'ml-auto inline-flex items-center min-h-11 text-xs font-light px-2 rounded',
            confirming ? 'text-error' : 'text-muted hover:text-error',
            'transition-colors duration-200',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2',
          )}
        >
          {confirming ? 'Confirm?' : 'Delete'}
        </button>
      </div>
    </div>
  )
}
